import LecturerLayout from '../../components/LecturerLayout';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosInstance';
import { getInitialsAvatar, normalizeImagePath } from '../../utils/mediaUtils';
import { getImageURL } from '../../utils/api';

const findSubjectById = (groups, batchSubjectId) => {
    if (!batchSubjectId) return null;
    for (const group of groups) {
        const match = group.subjects.find(subject => subject.batchSubjectId === batchSubjectId);
        if (match) {
            return match;
        }
    }
    return null;
};

const formatDate = value => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const formatDateTime = value => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleString();
};

const formatDuration = minutes => {
    if (minutes === null || minutes === undefined) return '—';
    const total = Number(minutes);
    if (!Number.isFinite(total) || total <= 0) return '—';
    const hours = Math.floor(total / 60);
    const remaining = total % 60;
    if (!hours) {
        return `${remaining}m`;
    }
    return remaining ? `${hours}h ${remaining}m` : `${hours}h`;
};

const calculateDurationMinutes = (start, end) => {
    if (!start || !end) {
        return null;
    }

    const [startHours, startMinutes] = start.split(':').map((value) => parseInt(value, 10));
    const [endHours, endMinutes] = end.split(':').map((value) => parseInt(value, 10));

    if ([startHours, startMinutes, endHours, endMinutes].some((value) => Number.isNaN(value))) {
        return null;
    }

    const startTotal = startHours * 60 + startMinutes;
    const endTotal = endHours * 60 + endMinutes;
    const difference = endTotal - startTotal;

    if (difference <= 0) {
        return null;
    }

    return difference;
};

export default function MyFeedback({ subjectGroups }) {
    const [today] = useState(() => new Date().toISOString().split('T')[0]);
    const [groups, setGroups] = useState(subjectGroups ?? []);
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [subjectSearch, setSubjectSearch] = useState('');
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [students, setStudents] = useState([]);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [studentSearch, setStudentSearch] = useState('');
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [feedbackHistory, setFeedbackHistory] = useState([]);
    const [feedbackLoading, setFeedbackLoading] = useState(false);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        feedbackDate: today,
        startTime: '',
        endTime: '',
        feedback: '',
    });
    const [derivedDuration, setDerivedDuration] = useState(null);
    const [durationError, setDurationError] = useState(null);

    useEffect(() => {
        setGroups(subjectGroups ?? []);
    }, [subjectGroups]);

    const filteredGroups = useMemo(() => {
        if (!subjectSearch.trim()) {
            return groups;
        }
        const term = subjectSearch.toLowerCase();
        return groups
            .map(group => ({
                ...group,
                subjects: group.subjects.filter(subject => {
                    const haystack = [
                        subject.subjectName,
                        subject.subjectCode,
                        subject.courseName,
                        subject.courseShortName,
                        subject.batchName,
                        subject.whatsappGroupName,
                    ]
                        .filter(Boolean)
                        .join(' ')
                        .toLowerCase();
                    return haystack.includes(term);
                }),
            }))
            .filter(group => group.subjects.length > 0);
    }, [groups, subjectSearch]);

    const filteredStudents = useMemo(() => {
        if (!studentSearch.trim()) {
            return students;
        }
        const term = studentSearch.toLowerCase();
        return students.filter(student => {
            const haystack = [
                student.studentName,
                student.studentId,
                student.registrationNumber,
                student.studentEmail,
                student.mobileNumber,
                student.telephone,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return haystack.includes(term);
        });
    }, [students, studentSearch]);

    const refreshGroups = useCallback(async () => {
        setGroupsLoading(true);
        try {
            const response = await axiosInstance.get('/lecturer/feedback/subjects');
            const data = response?.data || {};

            if (!data.success) {
                throw new Error(data.error || data.message || 'Failed to refresh assigned subjects.');
            }

            const nextGroups = data.groups ?? [];
            setGroups(nextGroups);

            if (selectedSubject) {
                const updated = findSubjectById(nextGroups, selectedSubject.batchSubjectId);
                if (updated) {
                    setSelectedSubject(prev => (prev ? { ...prev, ...updated } : updated));
                } else {
                    setSelectedSubject(null);
                    setStudents([]);
                    setSelectedStudent(null);
                    setFeedbackHistory([]);
                }
            }
        } catch (error) {
            console.error(error);
            const backendMessage = error?.response?.data?.message || error?.response?.data?.error;
            Swal.fire('Error', backendMessage || error?.message || 'Unable to refresh subjects.', 'error');
        } finally {
            setGroupsLoading(false);
        }
    }, [selectedSubject]);

    const resetForm = useCallback(() => {
        setFormData({
            feedbackDate: today,
            startTime: '',
            endTime: '',
            feedback: '',
        });
        setDerivedDuration(null);
        setDurationError(null);
    }, [today]);

    useEffect(() => {
        if (!formData.startTime || !formData.endTime) {
            setDerivedDuration(null);
            setDurationError(null);
            return;
        }

        const duration = calculateDurationMinutes(formData.startTime, formData.endTime);

        if (duration === null) {
            setDerivedDuration(null);
            setDurationError('End time must be later than start time.');
        } else {
            setDerivedDuration(duration);
            setDurationError(null);
        }
    }, [formData.startTime, formData.endTime]);

    useEffect(() => {
        // Load initial subject groups on first render
        // We intentionally call refreshGroups only once here to avoid
        // triggering a refresh loop when selectedSubject changes.
        refreshGroups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubjectSelect = useCallback(async subject => {
        setSelectedSubject(subject);
        setStudentsLoading(true);
        setStudents([]);
        setSelectedStudent(null);
        setFeedbackHistory([]);
        setStudentSearch('');
        resetForm();

        try {
            const response = await axiosInstance.get(`/lecturer/feedback/subjects/${subject.batchSubjectId}/students`);
            const data = response?.data || {};

            if (!data.success) {
                throw new Error(data.error || data.message || 'Failed to load students for the selected subject.');
            }

            const fetchedStudents = data.students ?? [];
            setStudents(fetchedStudents);

            if (data.subject) {
                setSelectedSubject(prev => {
                    const payload = { ...subject, ...data.subject };
                    return prev && prev.batchSubjectId === payload.batchSubjectId ? { ...prev, ...payload } : payload;
                });
                setGroups(prev => prev.map(group => ({
                    ...group,
                    subjects: group.subjects.map(item =>
                        item.batchSubjectId === data.subject.batchSubjectId ? { ...item, ...data.subject } : item,
                    ),
                })));
            }
        } catch (error) {
            console.error(error);
            setSelectedSubject(null);
            Swal.fire('Error', error?.message || 'Unable to load students.', 'error');
        } finally {
            setStudentsLoading(false);
        }
    }, [resetForm]);

    const clearSubjectSelection = useCallback(() => {
        setSelectedSubject(null);
        setStudents([]);
        setSelectedStudent(null);
        setFeedbackHistory([]);
        setStudentSearch('');
        resetForm();
    }, [resetForm]);

    const handleStudentSelect = useCallback(async student => {
        if (!selectedSubject) return;
        setSelectedStudent(student);
        setFeedbackLoading(true);
        setFeedbackHistory([]);

        try {
            const response = await axiosInstance.get(
                `/lecturer/feedback/subjects/${selectedSubject.batchSubjectId}/students/${encodeURIComponent(student.studentId)}/history`,
            );
            const data = response?.data || {};

            if (!data.success) {
                throw new Error(data.error || data.message || 'Failed to load feedback history.');
            }

            const latestStudent = {
                ...student,
                ...(data.student ?? {}),
            };
            setSelectedStudent(latestStudent);
            setFeedbackHistory(data.feedback ?? []);
        } catch (error) {
            console.error(error);
            Swal.fire('Error', error?.message || 'Unable to load feedback history.', 'error');
        } finally {
            setFeedbackLoading(false);
        }
    }, [selectedSubject]);

    const handleFormInput = useCallback(field => event => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    const handleSubmitFeedback = useCallback(async event => {
        event.preventDefault();
        if (!selectedSubject || !selectedStudent) {
            Swal.fire('Select Student', 'Please choose a subject and a student before adding feedback.', 'info');
            return;
        }

        if (durationError) {
            Swal.fire('Check Times', durationError, 'warning');
            return;
        }

        if (!formData.feedback.trim()) {
            Swal.fire('Feedback Required', 'Add your feedback notes before submitting.', 'warning');
            return;
        }

        setFormSubmitting(true);
        try {
            const payload = {
                feedback: formData.feedback.trim(),
                feedback_date: formData.feedbackDate,
                start_time: formData.startTime || null,
                end_time: formData.endTime || null,
                duration_minutes: derivedDuration,
            };

            const response = await axiosInstance.post(
                `/lecturer/feedback/subjects/${selectedSubject.batchSubjectId}/students/${encodeURIComponent(selectedStudent.studentId)}`,
                payload,
            );

            const data = response?.data || {};

            if (!data.success) {
                throw new Error(data.error || data.message || 'Failed to save feedback.');
            }

            if (data.entry) {
                setFeedbackHistory(prev => [data.entry, ...prev]);
            } else {
                await handleStudentSelect(selectedStudent);
            }

            if (data.stats) {
                setStudents(prev => prev.map(item => (
                    item.studentId === selectedStudent.studentId
                        ? {
                            ...item,
                            lastFeedbackDate: data.stats.lastFeedbackDate ?? item.lastFeedbackDate,
                            feedbackCount:
                                typeof data.stats.feedbackCount === 'number'
                                    ? data.stats.feedbackCount
                                    : item.feedbackCount,
                        }
                        : item
                )));
                setSelectedStudent(prev => (prev ? {
                    ...prev,
                    lastFeedbackDate: data.stats.lastFeedbackDate ?? prev.lastFeedbackDate,
                    feedbackCount:
                        typeof data.stats.feedbackCount === 'number'
                            ? data.stats.feedbackCount
                            : prev.feedbackCount,
                } : prev));
            }

            Swal.fire('Saved', data.message || 'Feedback recorded successfully.', 'success');
            resetForm();
        } catch (error) {
            console.error(error);
            Swal.fire('Error', error?.message || 'Unable to save feedback.', 'error');
        } finally {
            setFormSubmitting(false);
        }
    }, [derivedDuration, durationError, formData, handleStudentSelect, resetForm, selectedStudent, selectedSubject]);

    const derivedDurationLabel = formatDuration(derivedDuration);

    return (
        <LecturerLayout title="My Feedback">
            <div className="mx-auto max-w-7xl space-y-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">My Feedback</h1>
                        <p className="mt-1 text-sm text-gray-600">
                            Review your assigned subjects by WhatsApp group, browse enrolled students, and keep a professional
                            feedback trail.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={refreshGroups}
                        disabled={groupsLoading}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-green-500 hover:text-green-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {groupsLoading ? (
                            <svg className="h-4 w-4 animate-spin text-green-600" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                />
                            </svg>
                        ) : (
                            <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9M20 20v-5h-.581m-15.357-2a8.003 8.003 0 0015.938 2" />
                            </svg>
                        )}
                        <span>Refresh Subjects</span>
                    </button>
                </div>

                <div className="grid gap-6 lg:grid-cols-[320px_1fr] xl:grid-cols-[360px_1fr]">
                    <aside className="space-y-6 lg:sticky lg:top-24">
                        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-semibold text-gray-900">Assigned Subjects</h2>
                                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                                    {groups.length} groups
                                </span>
                            </div>
                            {selectedSubject ? (
                                <div className="mt-3">
                                    <div className="flex items-center justify-between gap-3 rounded-lg border border-green-500 bg-green-50 px-3 py-3 shadow-sm">
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-semibold text-gray-900">{selectedSubject.subjectName}</p>
                                            <p className="truncate text-xs text-gray-500">
                                                {selectedSubject.subjectCode}
                                                {selectedSubject.courseShortName || selectedSubject.courseName
                                                    ? ` • ${selectedSubject.courseShortName || selectedSubject.courseName}`
                                                    : ''}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={clearSubjectSelection}
                                            className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-green-300 bg-white px-3 py-1 text-xs font-medium text-green-700 shadow-sm hover:bg-green-100"
                                        >
                                            <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                <path
                                                    fillRule="evenodd"
                                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="mt-3">
                                        <input
                                            type="text"
                                            value={subjectSearch}
                                            onChange={event => setSubjectSearch(event.target.value)}
                                            placeholder="Search subject, course, or batch"
                                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
                                        />
                                    </div>
                                    <div className="mt-4 max-h-[calc(100vh-260px)] space-y-4 overflow-y-auto pr-1">
                                        {filteredGroups.length === 0 ? (
                                            <div className="rounded-lg border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500">
                                                No subjects matched your search. Adjust the filter or refresh the list.
                                            </div>
                                        ) : (
                                            filteredGroups.map(group => (
                                                <div key={group.groupKey}>
                                                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                        {group.groupLabel || 'Ungrouped'}
                                                    </div>
                                                    <div className="mt-2 space-y-2">
                                                        {group.subjects.map(subject => (
                                                            <button
                                                                key={subject.batchSubjectId}
                                                                type="button"
                                                                onClick={() => handleSubjectSelect(subject)}
                                                                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-3 text-left transition hover:border-green-300 hover:bg-green-50"
                                                            >
                                                                <div className="flex items-start justify-between gap-3">
                                                                    <div>
                                                                        <p className="text-sm font-semibold text-gray-900">{subject.subjectName}</p>
                                                                        <p className="text-xs text-gray-500">
                                                                            {subject.subjectCode}
                                                                            {subject.courseShortName || subject.courseName
                                                                                ? ` • ${subject.courseShortName || subject.courseName}`
                                                                                : ''}
                                                                        </p>
                                                                        <div className="mt-1 flex flex-wrap gap-1 text-[11px] text-gray-400">
                                                                            {subject.batchName && <span>Batch: {subject.batchName}</span>}
                                                                            {subject.whatsappGroupName && (
                                                                                <span className="inline-flex items-center gap-1">
                                                                                    <svg className="h-3 w-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                                                                        <path d="M2.003 5.884L10 1l7.997 4.884v8.232L10 19l-7.997-4.884V5.884z" />
                                                                                    </svg>
                                                                                    {subject.whatsappGroupName}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className="text-right">
                                                                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700">
                                                                            {subject.activeStudentCount} students
                                                                        </span>
                                                                        {subject.semester && (
                                                                            <p className="mt-1 text-[11px] text-gray-400">
                                                                                Semester {subject.semester}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </>
                            )}
                        </section>

                        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-semibold text-gray-900">Students</h2>
                                {selectedSubject && (
                                    <span className="truncate text-xs text-gray-500" title={selectedSubject.subjectName}>
                                        {selectedSubject.subjectName}
                                    </span>
                                )}
                            </div>
                            <div className="mt-3">
                                <input
                                    type="text"
                                    value={studentSearch}
                                    onChange={event => setStudentSearch(event.target.value)}
                                    disabled={!selectedSubject || studentsLoading}
                                    placeholder="Search student by name or ID"
                                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200 disabled:cursor-not-allowed disabled:bg-gray-100"
                                />
                            </div>
                            <div className="mt-4 max-h-[calc(100vh-260px)] space-y-2 overflow-y-auto pr-1">
                                {!selectedSubject ? (
                                    <div className="rounded-lg border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500">
                                        Choose a subject to view enrolled students.
                                    </div>
                                ) : studentsLoading ? (
                                    <div className="space-y-2">
                                        {[...Array(6)].map((_, index) => (
                                            <div key={index} className="animate-pulse rounded-lg border border-gray-100 bg-gray-100 p-3">
                                                <div className="h-4 w-2/3 rounded bg-gray-200" />
                                            </div>
                                        ))}
                                    </div>
                                ) : filteredStudents.length === 0 ? (
                                    <div className="rounded-lg border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500">
                                        No students matched this filter.
                                    </div>
                                ) : (
                                    filteredStudents.map(student => {
                                        const isActive = selectedStudent?.studentId === student.studentId;
                                        return (
                                            <button
                                                key={student.studentId}
                                                type="button"
                                                onClick={() => handleStudentSelect(student)}
                                                className={`w-full rounded-lg border px-3 py-2 text-left transition ${
                                                    isActive
                                                        ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                                                        : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <img
                                                        src={getImageURL(student.studentImage) || ''}
                                                        alt={student.studentName}
                                                        className="h-10 w-10 rounded-full object-cover"
                                                    />
                                                    <div className="flex-1">
                                                        <p className="text-sm font-semibold text-gray-900">{student.studentName}</p>
                                                        <p className="text-xs text-gray-500">
                                                            {student.registrationNumber || student.studentId}
                                                        </p>
                                                        <p className="text-xs text-gray-400">
                                                            {student.lastFeedbackDate
                                                                ? `Last feedback: ${formatDate(student.lastFeedbackDate)}`
                                                                : 'No feedback recorded yet'}
                                                        </p>
                                                    </div>
                                                    <div className="text-right text-xs text-gray-500">
                                                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                                                            {student.feedbackCount} notes
                                                        </span>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </section>
                    </aside>

                    <div className="space-y-6">
                        <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">Feedback Notes</h2>
                                    {selectedSubject ? (
                                        <p className="text-sm text-gray-600">
                                            {selectedSubject.subjectName}
                                            {selectedSubject.batchName ? ` • ${selectedSubject.batchName}` : ''}
                                        </p>
                                    ) : (
                                        <p className="text-sm text-gray-500">Select a subject to begin capturing feedback.</p>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {selectedStudent && (
                                        <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
                                            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M10 10a3 3 0 100-6 3 3 0 000 6z" />
                                                <path fillRule="evenodd" d="M.458 16.042C1.732 12.943 4.652 11 10 11s8.268 1.943 9.542 5.042A1 1 0 0118.618 18H1.382a1 1 0 01-.924-1.958z" clipRule="evenodd" />
                                            </svg>
                                            {selectedStudent.studentName}
                                        </span>
                                    )}
                                    {selectedStudent && (
                                        <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                                            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M2.003 5.884 10 1l7.997 4.884v8.232L10 19l-7.997-4.884V5.884z" />
                                            </svg>
                                            {selectedStudent.registrationNumber || selectedStudent.studentId}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {selectedStudent && (
                                <div className="mt-4 grid gap-3 rounded-lg border border-gray-100 bg-gray-50 p-4 text-xs text-gray-600 sm:grid-cols-2">
                                    <div>
                                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Email</p>
                                        <p className="text-sm text-gray-700">{selectedStudent.studentEmail || '—'}</p>
                                    </div>
                                    <div>
                                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Phone</p>
                                        <p className="text-sm text-gray-700">{selectedStudent.mobileNumber || selectedStudent.telephone || '—'}</p>
                                    </div>
                                    <div>
                                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Feedback Entries</p>
                                        <p className="text-sm text-gray-700">{selectedStudent.feedbackCount}</p>
                                    </div>
                                    <div>
                                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Last Noted</p>
                                        <p className="text-sm text-gray-700">{selectedStudent.lastFeedbackDate ? formatDate(selectedStudent.lastFeedbackDate) : '—'}</p>
                                    </div>
                                </div>
                            )}

                            <form className="mt-6 space-y-4" onSubmit={handleSubmitFeedback}>
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                                    <div>
                                        <label className="text-xs font-medium uppercase tracking-wide text-gray-500">Feedback Date</label>
                                        <input
                                            type="date"
                                            value={formData.feedbackDate}
                                            readOnly
                                            disabled
                                            className="mt-1 w-full cursor-not-allowed rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-600"
                                        />
                                        <p className="mt-1 text-[11px] text-gray-400">Feedback is always recorded with today&rsquo;s date.</p>
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium uppercase tracking-wide text-gray-500">Start Time</label>
                                        <input
                                            type="time"
                                            value={formData.startTime}
                                            onChange={handleFormInput('startTime')}
                                            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium uppercase tracking-wide text-gray-500">End Time</label>
                                        <input
                                            type="time"
                                            value={formData.endTime}
                                            onChange={handleFormInput('endTime')}
                                            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium uppercase tracking-wide text-gray-500">Duration</label>
                                        <div className="mt-1 flex h-10 items-center justify-between rounded-lg border border-dashed border-gray-200 px-3 text-sm text-gray-600">
                                            <span>{derivedDurationLabel}</span>
                                            <span className="text-xs text-gray-400">auto</span>
                                        </div>
                                        <p className="mt-1 text-[11px] text-gray-400">Calculated from start and end time.</p>
                                    </div>
                                </div>

                                {durationError && (
                                    <p className="text-xs font-semibold text-red-600">{durationError}</p>
                                )}

                                <div>
                                    <label className="text-xs font-medium uppercase tracking-wide text-gray-500">Feedback Notes</label>
                                    <textarea
                                        value={formData.feedback}
                                        onChange={handleFormInput('feedback')}
                                        rows={5}
                                        placeholder="Summarize your discussion in a formal and constructive tone..."
                                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
                                        disabled={!selectedSubject || !selectedStudent}
                                        required
                                    />
                                </div>

                                <div className="flex flex-wrap justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => resetForm()}
                                        disabled={!selectedSubject || !selectedStudent || formSubmitting}
                                        className="inline-flex items-center rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        Clear
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={!selectedSubject || !selectedStudent || formSubmitting}
                                        className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {formSubmitting && (
                                            <svg className="h-4 w-4 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                            </svg>
                                        )}
                                        <span>Save Feedback</span>
                                    </button>
                                </div>
                            </form>
                        </section>

                        <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-semibold text-gray-900">Previous Feedback</h2>
                                {feedbackHistory.length > 0 && (
                                    <span className="text-xs text-gray-500">{feedbackHistory.length} entries</span>
                                )}
                            </div>

                            <div className="mt-4 space-y-3">
                                {feedbackLoading ? (
                                    <div className="space-y-2">
                                        {[...Array(4)].map((_, index) => (
                                            <div key={index} className="animate-pulse rounded-lg border border-gray-100 bg-gray-100 p-4">
                                                <div className="h-4 w-1/2 rounded bg-gray-200" />
                                                <div className="mt-2 h-3 w-full rounded bg-gray-200" />
                                            </div>
                                        ))}
                                    </div>
                                ) : !selectedStudent ? (
                                    <div className="rounded-lg border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500">
                                        Pick a student to review their feedback history.
                                    </div>
                                ) : feedbackHistory.length === 0 ? (
                                    <div className="rounded-lg border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500">
                                        No feedback recorded yet. Use the form above to add the first note.
                                    </div>
                                ) : (
                                    feedbackHistory.map(entry => (
                                        <article key={entry.id} className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm">
                                            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900">
                                                        {formatDate(entry.feedbackDate) || 'Date not recorded'}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {entry.startTime || entry.endTime
                                                            ? `${entry.startTime ?? '—'} - ${entry.endTime ?? '—'}`
                                                            : 'Time not recorded'}
                                                    </p>
                                                </div>
                                                <div className="text-right text-xs text-gray-500">
                                                    <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700">
                                                        {formatDuration(entry.durationMinutes)}
                                                    </span>
                                                    {entry.createdAt && (
                                                        <p className="mt-1">Logged {formatDateTime(entry.createdAt)}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <p className="mt-3 whitespace-pre-line text-sm text-gray-700">{entry.feedback}</p>
                                        </article>
                                    ))
                                )}
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </LecturerLayout>
    );
}
